import React, { useMemo } from 'react';
import { scaleLinear } from '@visx/scale';
import RangeSpan from 'js/engines/CapabilitiesAssessment/components/Review/Charts/CapabilityResultsChart/RangeSpan';

import styles from './CapabilityScoreBar.module.scss';

/**
 * Duplicate of CapabilityResultBar for AssessmentSummary cards.
 * Same scoring calculations as the review chart; visual style is separate for later customization.
 */
const margin = {
  top: 4,
  right: 0,
  bottom: 4,
  left: 0,
};

const CapabilityScoreBar = ({
  segmentWidth = 20,
  segmentSpacing = 2,
  height = 36,
  elementMetrics,
  capabilityElementMap,
  capability,
}) => {
  const { width, chartHeight } = useMemo(() => {
    const width =
      (segmentWidth + segmentSpacing) * Object.keys(elementMetrics).length -
      segmentSpacing +
      margin.left +
      margin.right;
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;
    return { width, chartWidth, chartHeight };
  }, [elementMetrics, segmentWidth, segmentSpacing, height]);

  const capabilityElements = Object.values(capabilityElementMap).sort(
    (a, b) => a.paletteIndex - b.paletteIndex
  );

  const scaleY = useMemo(
    () =>
      scaleLinear({
        domain: [0, 1],
        range: [chartHeight, 0],
      }),
    [chartHeight]
  );

  return (
    <svg
      className={styles['assessment-summary-capability-bar']}
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
    >
      <g transform={`translate(${margin.left}, ${margin.top})`}>
        {capabilityElements.map((ce, i) => {
          const ceMetrics = elementMetrics[ce.key];
          if (!ceMetrics) {
            return null;
          }
          const { minScore, maxScore, normalizedScore } = ceMetrics;
          const hasValidScores =
            typeof minScore === 'number' &&
            Number.isFinite(minScore) &&
            typeof maxScore === 'number' &&
            Number.isFinite(maxScore);
          const hasValidMean =
            typeof normalizedScore === 'number' &&
            Number.isFinite(normalizedScore);

          return (
            <g
              key={ce.key}
              transform={`translate(${i * (segmentWidth + segmentSpacing)}, 0)`}
              className={`${styles['segment']} palette-chart-${
                capabilityElementMap[ce.key].paletteIndex
              }`}
            >
              <line
                x1={segmentWidth / 2}
                x2={segmentWidth / 2}
                y={0}
                y2={chartHeight}
                className={styles['slider-track']}
              />
              {hasValidScores && (
                <RangeSpan
                  x={segmentWidth / 2 - 2}
                  width={4}
                  min={scaleY(maxScore)}
                  max={scaleY(minScore)}
                  minimumDifference={6}
                  absoluteMin={0}
                  absoluteMax={chartHeight}
                />
              )}
              {hasValidMean ? (
                <g>
                  <rect
                    x={segmentWidth / 2 - 5}
                    y={scaleY(normalizedScore) - 1.5}
                    width={10}
                    height={3}
                    className="palette-fill"
                    rx={2}
                    ry={2}
                  />
                  {/* <circle
                    cx={segmentWidth / 2}
                    cy={scaleY(normalizedScore)}
                    r={4}
                    className="palette-fill"
                  />
                  <circle
                    cx={segmentWidth / 2}
                    cy={scaleY(normalizedScore)}
                    r={2}
                    className={styles['dot']}
                  /> */}
                </g>
              ) : (
                <circle
                  cx={segmentWidth / 2}
                  cy={chartHeight / 2}
                  r={4}
                  className={styles['null-marker']}
                />
              )}
            </g>
          );
        })}
      </g>
    </svg>
  );
};

export default CapabilityScoreBar;
